import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLine, Reveal } from "../lib/motion";
import { STATS } from "../data";

const HERO_IMG =
  "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwbmV1cmFsJTIwbmV0d29yayUyMGFic3RyYWN0fGVufDB8fHx8MTc4NTQzOTQ5OHww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex min-h-screen items-center overflow-hidden pt-32"
      data-testid="hero-section"
    >
      {/* ambient accent glow */}
      <div className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[var(--accent)]/[0.10] blur-[120px]" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[380px] w-[380px] rounded-full bg-[var(--accent)]/[0.06] blur-[120px]" />

      {/* floating framed AI visual */}
      <motion.div
        style={{ y: imgY, scale: imgScale, opacity: fade }}
        className="pointer-events-none absolute right-6 top-1/2 hidden w-[38%] max-w-[540px] -translate-y-1/2 lg:block xl:right-16"
      >
        <div className="spotlight-img relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-black/10 card-shadow">
          <img
            src={HERO_IMG}
            alt="Neural intelligence mesh"
            className="h-full w-full object-cover"
          />
          <div className="glass absolute bottom-5 left-5 inline-flex items-center gap-2.5 rounded-full border border-black/10 px-4 py-2">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--accent)]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-700">
              Autonomous · live
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div style={{ y: contentY, opacity: fade }} className="container-vx relative">
        <Reveal delay={0.3}>
          <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-black/10 bg-white/70 px-4 py-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--accent)] shadow-[0_0_10px_rgba(36,86,255,0.6)]" />
            <span className="label-mono text-neutral-600">Autonomous AI, in production</span>
          </div>
        </Reveal>

        <h1 className="max-w-4xl font-sans text-[13vw] font-light leading-[0.92] tracking-tighter sm:text-7xl md:text-8xl lg:text-[8rem]">
          <MaskLine delay={0.35}>Intelligent</MaskLine>
          <MaskLine delay={0.47}>
            automation,{" "}
            <span className="italic text-neutral-400">engineered.</span>
          </MaskLine>
        </h1>

        <Reveal delay={0.8} className="mt-10 max-w-xl">
          <p className="font-body text-lg leading-relaxed text-neutral-600">
            Vertex Automation designs, deploys and scales autonomous AI systems
            for the world's most ambitious companies — turning operations into
            an intelligent, self-improving advantage.
          </p>
        </Reveal>

        <Reveal delay={0.95} className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href="#contact"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-black px-7 py-3.5 font-body font-medium text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            Book a Strategy Call
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#work"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 font-body text-black transition-colors duration-300 hover:border-black/40 hover:bg-black/[0.03]"
          >
            See the work
          </a>
        </Reveal>

        {/* Stats strip */}
        <Reveal delay={1.1} className="mt-20 grid max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-black/10 bg-black/10 sm:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="bg-white p-5">
              <div className="font-sans text-2xl font-semibold tracking-tight md:text-3xl">
                {s.value}
              </div>
              <div className="mt-1 font-body text-xs leading-snug text-neutral-500">
                {s.label}
              </div>
            </div>
          ))}
        </Reveal>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="label-mono text-[10px]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce text-neutral-400" />
      </motion.div>
    </section>
  );
};
